import type { AxiosInstance } from 'axios';
import type { GetOpportunitiesParams, GetOpportunitiesRes } from '../interfaces/api';
import { BlocksApi } from './blocks';
import { ConfigurationsApi } from './configurations';
import { OpportunityDetailApi } from './opportunityDetail';
import { PipelineAccessApi } from './pipelineAccess';

const DEFAULT_BASE_URL = '/crm/v1';

/**
 * Thin, typed HTTP client for the CRM backend (`crm_service`).
 *
 * It takes an `AxiosInstance` as a constructor argument rather than importing the shell's
 * `@/api/api` — this
 * keeps `frontend_crm` free of any compile-time dependency on the shell. In the shell, the instance
 * passed in is `$rq.authClient`, so requests inherit the token interceptor and 401-refresh/retry
 * queue; the standalone dev harness passes a plain `axios.create()` with a stub token.
 */
export class AqCrmRequests {
    private readonly client: AxiosInstance;

    private readonly baseURL: string;

    /** Nav: Advanced config — the block library. */
    readonly blocks: BlocksApi;

    /** Nav: Configuration CRM — stages & blocks, pipeline & fields. */
    readonly configurations: ConfigurationsApi;

    /** Nav: Opportunity — wizard reads/writes and prospects. */
    readonly opportunityDetail: OpportunityDetailApi;

    /** Nav: CRM access — which members see which pipeline. */
    readonly pipelineAccess: PipelineAccessApi;

    constructor(client: AxiosInstance, baseURL: string = process.env.VUE_APP_CRM_API_URL ?? DEFAULT_BASE_URL) {
        this.client = client;
        this.baseURL = baseURL;
        this.blocks = new BlocksApi(client, this.baseURL);
        this.configurations = new ConfigurationsApi(client, this.baseURL);
        this.opportunityDetail = new OpportunityDetailApi(client, this.baseURL);
        this.pipelineAccess = new PipelineAccessApi(client, this.baseURL);
    }

    async getOpportunities(params: GetOpportunitiesParams): Promise<GetOpportunitiesRes> {
        const response = await this.client.get<GetOpportunitiesRes>(`${this.baseURL}/opportunities`, {
            params,
        });
        return response.data;
    }
}

let crmRequests: AqCrmRequests | null = null;

/**
 * Stores the injected axios instance (and optional base URL override) in a module-level singleton.
 * Must be called once, before any CRM store action runs, by whichever host embeds this module.
 */
export function setCrmHttpClient(client: AxiosInstance, baseURL?: string): void {
    crmRequests = new AqCrmRequests(client, baseURL);
}

/**
 * Accessor used internally by the CRM Vuex store. Throws if `setCrmHttpClient()` has not run yet —
 * this is a programming error in whichever host embeds the module (shell or dev harness), not a
 * runtime condition the UI should try to recover from.
 */
export function getCrmRequests(): AqCrmRequests {
    if (!crmRequests) {
        throw new Error(
            'CRM HTTP client has not been initialized. Call setCrmHttpClient() before using the CRM module.',
        );
    }
    return crmRequests;
}
